import React from 'react';
import { ErrorPage } from '../../../auth/pages/Error';
import { NoAccess, NoAccessPatient } from '../../../auth/pages/NoAccess';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorType?: 'error' | 'no_access_patient' | 'no_access_user';
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode; redirectTo?: string },
  ErrorBoundaryState
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null, errorType: 'error' };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error, errorType: 'error' };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Error caught by ErrorBoundary:', error, info);
  }

  componentDidMount() {
    window.addEventListener('global-app-error', this.handleGlobalError);
    window.addEventListener('global-no-access-user', this.handleUserNoAccess);
    window.addEventListener(
      'global-no-access-patient',
      this.handleNoAccessPatient,
    );
  }

  componentWillUnmount() {
    window.removeEventListener('global-app-error', this.handleGlobalError);
    window.removeEventListener(
      'global-no-access-user',
      this.handleUserNoAccess,
    );
    window.removeEventListener(
      'global-no-access-patient',
      this.handleNoAccessPatient,
    );
  }

  handleGlobalError = (event: Event) => {
    try {
      const detail = (event as CustomEvent).detail || {};
      const message = detail.message || 'Error';
      this.setState({
        hasError: true,
        error: new Error(message),
        errorType: 'error',
      });
    } catch {
      this.setState({ hasError: true, errorType: 'error' });
    }
  };

  handleUserNoAccess = (event: Event) => {
    try {
      const detail = (event as CustomEvent).detail || {};
      const message = detail.message || 'No user access';
      this.setState({
        hasError: true,
        error: new Error(message),
        errorType: 'no_access_user',
      });
    } catch {
      this.setState({ hasError: true, errorType: 'no_access_user' });
    }
  };
  handleNoAccessPatient = (event: Event) => {
    try {
      const detail = (event as CustomEvent).detail || {};
      const message = detail.message || 'No access';
      this.setState({
        hasError: true,
        error: new Error(message),
        errorType: 'no_access_patient',
      });
    } catch {
      this.setState({ hasError: true, errorType: 'no_access_patient' });
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.state.errorType === 'no_access_patient') {
        return <NoAccessPatient />;
      }
      if (this.state.errorType === 'no_access_user') {
        return <NoAccess />;
      }
      if (this.props.redirectTo) {
        window.location.href = this.props.redirectTo;
        return null;
      }
      return <ErrorPage pageKey={'error'} />;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
